import React, { memo, useRef, useState } from 'react';
import { Container } from 'components/Container';
import { LogoAndLabel } from 'components/LogoAndLabel/LogoAndLabel';
import { OpInput } from 'new-components/DLS/OpInput/OpInput';
import { OpCheckbox } from 'new-components/DLS/OpCheckbox/OpCheckbox';
import { Button } from 'components/Button';
import { useTranslation } from 'react-i18next';
import { OpInfo } from 'new-components/DLS/OpInfo/OpInfo';
import OPProxyClient from './OPProxyClient';

const openpathConfig = require('openpathConfig');

// TODO reactify the form here to avoid document.querySelector()...

const LocalAdminPage = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [httpRequest, setHttpRequest] = useState({});
  const [httpResponse, setHttpResponse] = useState({});

  const log = (msg) => {
    setLogs((prevLogs) => prevLogs.concat(msg));
  };

  const c = useRef(new OPProxyClient(log));

  const onSetClicked = async () => {
    setLogs([]);
    await c.current.connect(log);
    try {
      const uri = 'http://localhost:8080/network';
      const headers = 'Content-Type: application/json\r\n';

      const netConfig = {
        interface: 'eth0',
        useDHCP: false,
        // address: '10.0.2.72/24',
        // gateway: '10.0.2.1',
        // nameservers: ['8.8.8.8']
      };

      netConfig.address = document.querySelector('#address').value;
      netConfig.gateway = document.querySelector('#gateway').value;
      netConfig.nameservers = [document.querySelector('#nameserver').value];
      netConfig.useDHCP = document.querySelector('input[name=dhcp]').checked;

      setHttpRequest({ body: JSON.stringify(netConfig) });

      const result = await c.current.post(
        uri,
        headers,
        JSON.stringify(netConfig),
      );
      setHttpResponse(result);
    } catch (error) {
      log(`Error: ${error}`);
    }
  };

  const onGetClicked = async () => {
    setLogs([]);
    await c.current.connect();

    const headers = 'Content-Type: application/json\r\n';

    const result = await c.current.get(
      'http://localhost:8080/status/network',
      headers,
      '',
    );
    const netStatus = JSON.parse(result.body);
    document.querySelector('#address').value = netStatus.address;
    document.querySelector('#gateway').value = netStatus.gateway;
    setHttpResponse(result);
  };

  const renderContent = () => {
    const haveBluetooth = navigator.bluetooth;
    if (!haveBluetooth) {
      return (
        <div id="output" className="output">
          {t(
            `This page requires the Web Bluetooth API, which is not available in your current browser. Please make sure you are using `,
          )}
          <a href="https://platform-status.mozilla.org/#web-bluetooth">
            {t(`a supported browser`)}
          </a>
          {t(` and have enabled the "Experimental Web Platform features" flag`)}
          .
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 11 }}>
        {t(`Address:`)}
        <OpInput
          style={{ marginBottom: 11, marginTop: 8 }}
          id="address"
          name="address"
          maxLength={512}
        />
        {t(`Gateway:`)}
        <OpInput
          style={{ marginBottom: 11, marginTop: 8 }}
          id="gateway"
          name="gateway"
          maxLength={512}
        />
        {t(`Nameserver:`)}
        <OpInput
          style={{ marginBottom: 11, marginTop: 8 }}
          id="nameserver"
          name="nameserver"
          maxLength={512}
          defaultValue="8.8.8.8"
        />
        <div>
          <OpCheckbox
            style={{ marginBottom: 11, marginRight: 11 }}
            name="dhcp"
          />
          {t(`DHCP`)}
        </div>
        <div>
          <Button
            primary
            content={t(`Get current network config`)}
            onClick={onGetClicked}
          />
          <Button
            primary
            content={t(`Update network config`)}
            onClick={onSetClicked}
          />
        </div>
        <h3 style={{ marginTop: 11 }}>{t(`HTTP Request`)}</h3>
        <div>
          {httpRequest.body && (
            <div>
              <h4>{t(`Body`)}</h4>
              {httpRequest.body}
            </div>
          )}
        </div>
        <h3>{t(`HTTP Response`)}</h3>
        <div>
          {httpResponse.status && (
            <div>
              <h4>{t(`Status`)}</h4>
              {httpResponse.status}
            </div>
          )}
          {httpResponse.headers && (
            <div>
              <h4>{t(`Headers`)}</h4>
              {httpResponse.headers}
            </div>
          )}
          {httpResponse.body && (
            <div>
              <h4>{t(`Body`)}</h4>
              {httpResponse.body}
            </div>
          )}
        </div>
        <h3>{t(`Log`)}</h3>
        <pre>{logs.map((x) => `${x}\n`)}</pre>
      </div>
    );
  };

  return (
    <div id="extr-page">
      <header
        id="header"
        className={`animated fadeInDown header-${openpathConfig.ENV}`}
      >
        <LogoAndLabel label={t(`ACU Local Administration`)} />
      </header>
      <div role="main" className="animated fadeInDown">
        <Container>
          <OpInfo
            message={t('Note')}
            description={
              <ul>
                <li>
                  {t(
                    `This page is for local network configuration and diagnostics for Openpath Smart Hub Access Control Units (ACUs).`,
                  )}
                </li>
                <li>
                  {t(
                    `In order to use this page, you must first press the 'ADMIN' button an Openpath ACU to temporarily enable its local network access mode.`,
                  )}
                </li>
                <li>
                  {t(
                    `If your ACU is already connected to the internet and activated, you can make configuration changes to the ACU through the normal Device / ACU Management section of the `,
                  )}
                  <a href="/">{t(`Access`)}</a>
                  {t(
                    `; you would only need to use this page if the ACU needs a different network configuration (for instance if you want to change from DHCP to a statically-assigned IP address).`,
                  )}
                </li>
              </ul>
            }
          />
          <h2 className="font-xl">
            <span className="op-font-weight-500">
              {t(`ACU Network Configuration`)}
            </span>
          </h2>
          {renderContent()}
        </Container>
      </div>
    </div>
  );
};

export default memo(LocalAdminPage);
